import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Upload } from 'antd';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './MultipleImageUpload.scss';

const MultipleImageUpload = ({ 
  images = [], 
  onImagesChange, 
  maxImages = 10, 
  maxSizeMB = 2,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp']
}) => {
  const { t } = useTranslation('global');
  const [previews, setPreviews] = useState([]);
  const prevImagesRef = useRef([]);

  // Инициализируем превью из переданных изображений
  useEffect(() => {
    // Проверяем, изменились ли изображения
    const imagesChanged = images.length !== prevImagesRef.current.length ||
      !images.every((img, i) => img === prevImagesRef.current[i]);

    if (!imagesChanged) {
      return; // Изображения не изменились, не обновляем
    }

    // Сохраняем текущие изображения для следующей проверки
    prevImagesRef.current = images;

    // Получаем текущие превью для освобождения старых blob URLs
    setPreviews(currentPreviews => {
      // Освобождаем старые blob URLs
      currentPreviews.forEach(preview => {
        if (preview.preview && preview.preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview.preview);
        }
      });

      if (images && images.length > 0) {
        // Создаем превью для всех изображений
        const newPreviews = images.map((image, index) => {
          // Если это File объект, создаем превью
          if (image instanceof File) {
            return {
              id: `file-${image.name}-${image.size}-${index}`,
              file: image,
              preview: URL.createObjectURL(image),
              name: image.name
            };
          }
          // Если это строка (URL), используем её как превью
          else if (typeof image === 'string') {
            return {
              id: `url-${index}-${Date.now()}`,
              file: null, // Не File объект, это URL
              preview: image,
              name: `Image ${index + 1}`
            };
          }
          // Если это объект с preview
          else if (image && image.preview) {
            return {
              id: image.id || `obj-${index}-${Date.now()}`,
              file: image.file || null,
              preview: image.preview,
              name: image.name || `Image ${index + 1}`
            };
          }
          return null;
        }).filter(Boolean); // Удаляем null значения

        return newPreviews;
      } else {
        // Если изображений нет, очищаем превью
        return [];
      }
    });
    
    // Cleanup: освобождаем blob URLs при размонтировании
    return () => {
      setPreviews(currentPreviews => {
        currentPreviews.forEach(preview => {
          if (preview.preview && preview.preview.startsWith('blob:')) {
            URL.revokeObjectURL(preview.preview);
          }
        });
        return [];
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const validateFile = (file) => {
    // Проверка типа файла
    if (!acceptedTypes.includes(file.type)) {
      message.error(t('imageUpload.onlyFiles', { types: acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ') }));
      return false;
    }
    
    // Проверка размера файла
    const isLtMaxSize = file.size / 1024 / 1024 < maxSizeMB;
    if (!isLtMaxSize) {
      message.error(t('imageUpload.maxSizeError', { maxSize: maxSizeMB }));
      return false;
    }

    return true;
  };

  const handleImageUpload = (file) => {
    if (!validateFile(file)) {
      return false;
    }

    // Проверяем лимит изображений
    if (images.length >= maxImages) {
      message.error(t('imageUpload.maxImagesError', { maxImages }));
      return false;
    }

    // Создаем превью
    const reader = new FileReader();
    reader.onload = (e) => {
      const newPreview = {
        id: Date.now() + Math.random(),
        file: file,
        preview: e.target.result,
        name: file.name
      };
      
      const newPreviews = [...previews, newPreview];
      setPreviews(newPreviews);
      
      // Обновляем родительский компонент
      const newImages = [...images, file];
      onImagesChange(newImages);
    };
    reader.readAsDataURL(file);
    
    return false; // Предотвращаем автоматическую загрузку
  };

  const removeImage = (index) => {
    // Освобождаем blob URL перед удалением
    if (previews[index]?.preview && previews[index].preview.startsWith('blob:')) {
      URL.revokeObjectURL(previews[index].preview);
    }
    
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    setPreviews(newPreviews);
    onImagesChange(newImages);
  };

  const uploadButton = (
    <div className="upload-button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t('imageUpload.addPhoto')}</div>
      <div className="upload-hint">
        {t('imageUpload.uploadHint', { maxImages, maxSize: maxSizeMB })}
      </div>
    </div>
  );

  return (
    <div className="multiple-image-upload">
      <div className="upload-section">
        <Upload
          beforeUpload={handleImageUpload}
          showUploadList={false}
          accept={acceptedTypes.join(',')}
          multiple={false} // Загружаем по одному файлу
        >
          {images.length < maxImages ? uploadButton : null}
        </Upload>
      </div>

      {(previews.length > 0 || images.length > 0) && (
        <div className="images-gallery">
          <div className="gallery-header">
            <span className="gallery-title">{t('imageUpload.uploadedImages', { current: previews.length, max: maxImages })}</span>
          </div>
          <div className="gallery-grid">
            {previews.map((preview, index) => (
              <div key={preview.id} className="image-item">
                <div className="image-preview">
                  <img src={preview.preview} alt={preview.name} />
                  <div className="image-overlay">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeImage(index)}
                      className="remove-button"
                      title={t('imageUpload.deleteImage')}
                    />
                  </div>
                </div>
                <div className="image-info">
                  <span className="image-name" title={preview.name}>
                    {preview.name.length > 15 
                      ? `${preview.name.substring(0, 15)}...` 
                      : preview.name
                    }
                  </span>
                  <span className="image-size">
                    {(preview.file.size / 1024 / 1024).toFixed(1)}MB
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultipleImageUpload;
